import { useCallback, useMemo } from 'react';
import { EventPayload, RunEventTypes, ServerSideEventType } from '@/hooks/api/sse/types.ts';
import { useBaseSSE } from '@/hooks/api/sse/useBaseSSE.ts';
import useInvalidateStepExecution from '@/hooks/api/sse/runEvents/useInvalidateStepExecution.ts';
import useHandleEvent from '@/hooks/api/sse/useHandleEvent.ts';
import useRunId from '@/hooks/useRunId.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import {
  LATEST_STEP_RUNS_BASE_KEY,
  AUTOMATION_RUN_KEY,
  EXTRACT_ENTITIES_BASE_KEY,
  RUN_CHAT_MESSAGES_KEY,
  RUN_WITH_DATASETS_KEY,
  STEP_EXECUTIONS_BASE_KEY,
  STEP_RUN_LOG_PRESENTATION_KEY,
  STEP_RUNS_BASE_KEY,
  WAITING_STEP_DEPENDENCIES_BASE_KEY,
} from '@/utils/queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import useInvalidateRunChatMessages from '@/hooks/api/sse/runEvents/useInvalidateRunChatMessages.ts';
import useInvalidateStepExecutionLogs from '@/hooks/api/sse/runEvents/useInvalidateStepExecutionLogEntry.ts';
import useInvalidateExtractEntity from '@/hooks/api/sse/runEvents/useInvalidateExtractEntity.ts';
import { ChatMessage } from '@/types/runs.ts';
import { StepExecution, StepExecutionLogEntry, StepRun } from '@/types/stepExecutions.ts';
import { ExtractEntity, ExtractEntityField } from '@/types/extract.ts';
import useInvalidateStepRun from '@/hooks/api/sse/runEvents/useInvalidateStepRun.ts';
import useInvalidateExtractEntityField from '@/hooks/api/sse/runEvents/useInvalidateExtractEntityField.ts';
import useRetrieveRun from '@/hooks/api/runs/useRetrieveRun.ts';

// Get all enum values (event type strings)
const eventTypes = Object.values(RunEventTypes) as RunEventTypes[];

export const getStepExecutionBindingInvalidationKeys = (automationId: string, runId: string) => [
  RUN_WITH_DATASETS_KEY(automationId, runId),
  STEP_RUNS_BASE_KEY(automationId, runId),
];

export const getStepExecutionLogPresentationInvalidationKeys = (
  automationId: string,
  runId: string,
  metadata?: Record<string, unknown>,
) => {
  const stepRunId = metadata?.step_run_id;

  return typeof stepRunId === 'string' && stepRunId
    ? [STEP_RUN_LOG_PRESENTATION_KEY(automationId, runId, stepRunId)]
    : [];
};

export function useRunSSE() {
  const runId = useRunId();
  const automationId = useAutomationId();

  const { data: run } = useRetrieveRun();

  const enabled = useMemo(() => !!run, [run]);

  const invalidateStepRun = useInvalidateStepRun(automationId, runId);

  const invalidateStepExecution = useInvalidateStepExecution(automationId, runId);

  const invalidateExtractEntityErrorLogs = useInvalidateStepExecutionLogs(
    automationId,
    'extract_entity_errors',
  );

  const invalidateExtractEntity = useInvalidateExtractEntity(automationId);
  const invalidateExtractEntityField = useInvalidateExtractEntityField(automationId);

  const invalidateChatMessages = useInvalidateRunChatMessages(automationId, runId);

  const queryClient = useQueryClient();

  const getKeysToInvalidate = useCallback(
    (type: ServerSideEventType, data: EventPayload): unknown[][] | undefined => {
      switch (type) {
        case RunEventTypes.StepRun:
          return invalidateStepRun(data.payload as StepRun);
        case RunEventTypes.StepExecution:
          return invalidateStepExecution(data.payload as StepExecution);
        case RunEventTypes.StepExecutionInputDataset:
        case RunEventTypes.StepExecutionOutputDataset:
          return getStepExecutionBindingInvalidationKeys(automationId, runId);
        case RunEventTypes.StepExecutionLog:
          return getStepExecutionLogPresentationInvalidationKeys(
            automationId,
            runId,
            data.metadata,
          );
        case RunEventTypes.ExtractErrorLog:
          return invalidateExtractEntityErrorLogs(data.payload as StepExecutionLogEntry, data.id);
        case RunEventTypes.ChatMessage:
          return invalidateChatMessages(data.payload as ChatMessage);
        case RunEventTypes.ExtractEntity:
          return invalidateExtractEntity(data.payload as ExtractEntity, data.id);
        case RunEventTypes.ExtractEntityField:
          return invalidateExtractEntityField(data.payload as ExtractEntityField, data.id);
        default:
          console.warn('Unhandled event type:', type);
          return [];
      }
    },
    [
      automationId,
      invalidateStepRun,
      invalidateStepExecution,
      invalidateExtractEntityErrorLogs,
      invalidateChatMessages,
      invalidateExtractEntity,
      invalidateExtractEntityField,
      runId,
    ],
  );

  const onEvent = useHandleEvent(getKeysToInvalidate);

  const onConnect = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: AUTOMATION_RUN_KEY(automationId, runId) }),
      queryClient.invalidateQueries({ queryKey: RUN_WITH_DATASETS_KEY(automationId, runId) }),
      queryClient.invalidateQueries({ queryKey: RUN_CHAT_MESSAGES_KEY(automationId, runId) }),
      queryClient.invalidateQueries({ queryKey: LATEST_STEP_RUNS_BASE_KEY(automationId, runId) }),
      queryClient.invalidateQueries({ queryKey: STEP_RUNS_BASE_KEY(automationId, runId) }),
      queryClient.invalidateQueries({
        queryKey: WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
      }),
      // SSE is a coalesced latest-state stream, so reconnect still needs to refetch
      // any active execution/detail queries the page may have missed while disconnected.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const executionBaseKey = STEP_EXECUTIONS_BASE_KEY(automationId);
          return (
            query.isActive() &&
            executionBaseKey.every((part, index) => query.queryKey[index] === part)
          );
        },
      }),
      queryClient.invalidateQueries({
        predicate: (query) => {
          const extractEntitiesBaseKey = EXTRACT_ENTITIES_BASE_KEY(automationId);
          return (
            query.isActive() &&
            extractEntitiesBaseKey.every((part, index) => query.queryKey[index] === part)
          );
        },
      }),
    ]);
  }, [automationId, queryClient, runId]);

  const url = `${import.meta.env.VITE_API_BASE_URL}/sse/events/runs/${runId}`;

  return useBaseSSE(eventTypes, onEvent, onConnect, url, enabled);
}
